const GTM_CONTAINER_ID = 'GTM-T6GGSM8L';

/**
 * Checks if the debug logging mode is enabled in the widget configuration.
 *
 * @returns {boolean} True if debug mode is active.
 */
function isDebugEnabled() {
  const config = (window.MTA && window.MTA._config) || window.MTAConfig || {};
  return config.debug === true;
}

/**
 * Safely pushes an event and its parameters to the GA4 dataLayer.
 *
 * @param {string} eventName - The name of the event to push.
 * @param {Object} params - Additional event parameters.
 */
function safePush(eventName, params) {
  try {
    window.dataLayer = window.dataLayer || [];
    const conversationId = localStorage.getItem('mta_chat_id') || '';
    const payload = {
      event: eventName,
      conversation_id: conversationId,
      ...params,
    };
    window.dataLayer.push(payload);

    if (isDebugEnabled()) {
      console.log(`[GA4 Tracking] ${eventName}:`, payload);
    }
  } catch (error) {
    if (isDebugEnabled()) {
      console.error('[GA4 Tracking] Failed to record event:', error);
    }
  }
}

/**
 * The Tracking utility exports individual event dispatchers for the widget lifecycle.
 */
export const Tracking = {
  /**
   * Initializes the Google Tag Manager container dynamically.
   */
  init() {
    try {
      if (GTM_CONTAINER_ID && !document.getElementById('mta-gtm-script')) {
        const script = document.createElement('script');
        script.id = 'mta-gtm-script';
        script.innerHTML = `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${GTM_CONTAINER_ID}');
        `;
        document.head.appendChild(script);
        if (isDebugEnabled()) {
          console.log(`[GA4 Tracking] Initialized GTM Container: ${GTM_CONTAINER_ID}`);
        }
      }
    } catch (error) {
      if (isDebugEnabled()) {
        console.error('[GA4 Tracking] Failed to initialize GTM:', error);
      }
    }
  },

  /**
   * Tracks when the assistant drawer/inline panel is opened.
   *
   * @param {string} clientName - Name of the client configuration.
   */
  trackOpen(clientName) {
    safePush('miles_ai_assistant_open', {
      search_client: clientName || '',
    });
  },

  /**
   * Tracks when a user submits a message to the assistant.
   *
   * @param {string} clientName - Name of the client configuration.
   * @param {number} messageIndex - The 1-based index of the message in the conversation.
   * @param {string} searchQuery - The text content of the user query.
   * @param {string} messageSource - The origin source ('typed', 'suggested', or 'followup').
   */
  trackMessage(clientName, messageIndex, searchQuery, messageSource) {
    safePush('miles_ai_assistant_message', {
      search_client: clientName || '',
      message_index: messageIndex,
      search_query: searchQuery,
      message_source: messageSource,
    });
  },

  /**
   * Tracks when the assistant completes a response stream.
   *
   * @param {string} clientName - Name of the client configuration.
   * @param {number} messageIndex - The 1-based index of the query being answered.
   * @param {string} searchQuery - The text content of the query.
   * @param {string} responseStatus - Status of the response ('answered' or 'no_results').
   * @param {string} queryIntent - Type of response intent ('exploration' or 'direct').
   * @param {number} resultCount - Number of result listings returned.
   * @param {string} hadMap - 'true' or 'false' indicating if a map payload was displayed.
   * @param {number} searchResponseTime - Total response latency in milliseconds.
   */
  trackResponse(
    clientName,
    messageIndex,
    searchQuery,
    responseStatus,
    queryIntent,
    resultCount,
    hadMap,
    searchResponseTime
  ) {
    safePush('miles_ai_assistant_response', {
      search_client: clientName || '',
      message_index: messageIndex,
      search_query: searchQuery,
      response_status: responseStatus,
      query_intent: queryIntent,
      result_count: resultCount,
      had_map: hadMap,
      search_response_time: searchResponseTime,
    });
  },

  /**
   * Tracks when a user clicks a result (link) within an answer.
   *
   * @param {string} clientName - Name of the client configuration.
   * @param {string} resultTitle - The text or title of the clicked link.
   * @param {string} resultUrl - The destination URL of the link.
   * @param {string} [resultListingId] - Optional CMS listing ID.
   * @param {string} [resultType] - Optional type of the result ('listing', 'event', or 'deal').
   * @param {number} [resultPosition] - Optional 1-based position index of the link.
   */
  trackResultClick(
    clientName,
    resultTitle,
    resultUrl,
    resultListingId = undefined,
    resultType = undefined,
    resultPosition = undefined
  ) {
    const params = {
      search_client: clientName || '',
      result_title: resultTitle || '',
      result_url: resultUrl || '',
    };

    if (resultListingId !== undefined) {
      params.result_listing_id = resultListingId;
    }
    if (resultType !== undefined) {
      params.result_type = resultType;
    }
    if (resultPosition !== undefined) {
      params.result_position = resultPosition;
    }

    safePush('miles_ai_assistant_result_click', params);
  },
};
